import React, { useRef } from 'react';
import { MapPin, Check, Plus, Pencil } from 'lucide-react';
import { ONBOARDING_CITIES } from '../../../config/onboardingData';
import { useClickOutside } from './useClickOutside';

interface ProfileCitiesSelectorProps {
  selectedCities: string[];
  selectedZones: Record<string, string[]>;
  isEditing: boolean;
  onToggleEdit: () => void;
  onCloseEdit?: () => void;
  onToggleCity: (city: string) => void;
}

export const ProfileCitiesSelector: React.FC<ProfileCitiesSelectorProps> = ({
  selectedCities,
  selectedZones,
  isEditing,
  onToggleEdit,
  onCloseEdit,
  onToggleCity,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, onCloseEdit || onToggleEdit, isEditing);

  return (
    <div ref={containerRef} className="space-y-1.5">
      <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider">
        Ciudades Preferidas
      </label>

      {isEditing ? (
        <div className="w-full p-3.5 bg-white border-2 border-sky-500 ring-2 ring-sky-100 rounded-xl shadow-xs space-y-3 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-bold text-sky-800">
              Selecciona tus ciudades de operación:
            </span>
            <button
              type="button"
              onClick={onToggleEdit}
              title="Confirmar selección"
              className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
            >
              <Check className="w-4 h-4 stroke-[2.5]" />
            </button>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {ONBOARDING_CITIES.map((city) => {
              const isSelected = selectedCities.includes(city);
              return (
                <button
                  id={`btn-city-${city.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  key={city}
                  type="button"
                  onClick={() => onToggleCity(city)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer shadow-xs ${
                    isSelected
                      ? 'bg-sky-600 text-white shadow-sky-200'
                      : 'bg-slate-50 text-slate-700 border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50'
                  }`}
                >
                  {isSelected ? (
                    <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                  ) : (
                    <Plus className="w-3.5 h-3.5 text-slate-400" />
                  )}
                  <span>{city}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          onClick={onToggleEdit}
          title="Hacer clic para modificar ciudades"
          className="relative w-full pl-3.5 pr-10 py-2.5 bg-slate-200/80 hover:bg-slate-200 border border-slate-300/80 rounded-xl cursor-pointer transition-colors flex items-center min-h-[44px]"
        >
          {selectedCities.length === 0 ? (
            <span className="text-xs sm:text-sm text-slate-500 font-semibold">
              Sin ciudades seleccionadas (clic para elegir)
            </span>
          ) : (
            <div className="flex flex-wrap items-center gap-2 py-0.5">
              {selectedCities.map((city) => {
                const zoneCount = selectedZones[city]?.length || 0;
                return (
                  <span
                    key={city}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/90 border border-slate-300/70 rounded-lg text-xs sm:text-sm font-bold text-slate-800 shadow-2xs"
                  >
                    <MapPin className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                    <span>{city}</span>
                    <span className="text-[11px] font-extrabold text-sky-700 bg-sky-50 px-1.5 py-0.2 rounded ml-0.5">
                      {zoneCount > 0 ? `${zoneCount} ${zoneCount === 1 ? 'zona' : 'zonas'}` : 'Todas'}
                    </span>
                  </span>
                );
              })}
            </div>
          )}

          <div className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-600 pointer-events-none">
            <Pencil className="w-4 h-4" />
          </div>
        </div>
      )}
    </div>
  );
};
